import { useEffect, useRef, useState } from "react";
import { Play, Maximize2, ChevronLeft, ChevronRight, X } from "lucide-react";
import { useT } from "@/lib/i18n";
import { mediaUrl } from "@/lib/api";

function VideoPlayer({ m, alt }) {
  const { t } = useT();
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const src = mediaUrl(window.innerWidth < 900 ? m.p720 || m.p1080 : m.p1080 || m.p720);
  const fullscreen = () => (ref.current?.requestFullscreen || ref.current?.webkitEnterFullscreen)?.call(ref.current);
  return (
    <div className="photo-frame aspect-[4/5] w-full" data-testid="video-player">
      {playing ? (
        <video ref={ref} src={src} poster={mediaUrl(m.poster)} controls autoPlay playsInline controlsList="nodownload" className="absolute inset-0 w-full h-full object-contain bg-black" data-testid="video-element" />
      ) : (
        <button onClick={() => setPlaying(true)} className="absolute inset-0 group" aria-label={t.play_video} data-testid="video-play-button">
          {m.poster && <img src={mediaUrl(m.poster)} alt={alt} className="absolute inset-0 w-full h-full object-cover" />}
          <span className="absolute inset-0 m-auto w-20 h-20 rounded-full border border-[#C8AE6E]/70 bg-[#0C0B09]/50 backdrop-blur-md flex items-center justify-center group-hover:scale-105 transition-transform duration-500">
            <Play className="w-7 h-7 text-[#E6D5AC] translate-x-0.5" strokeWidth={1.2} />
          </span>
        </button>
      )}
      {playing && <button onClick={fullscreen} className="absolute top-3 right-3 p-2 rounded-full bg-[#0C0B09]/70 text-[#E6D5AC]" aria-label={t.fullscreen} data-testid="video-fullscreen-button"><Maximize2 className="w-4 h-4" /></button>}
    </div>
  );
}

function Zoomable({ m, alt, onOpen }) {
  const [pos, setPos] = useState(null);
  const move = (e) => {
    const b = e.currentTarget.getBoundingClientRect();
    setPos({ x: ((e.clientX - b.left) / b.width) * 100, y: ((e.clientY - b.top) / b.height) * 100 });
  };
  return (
    <button className="photo-frame grain aspect-[4/5] w-full cursor-zoom-in block" onMouseMove={move} onMouseLeave={() => setPos(null)} onClick={onOpen} data-testid="gallery-main-image">
      <img src={mediaUrl(m.lg || m.md)} alt={alt} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 ease-out"
        style={pos ? { transform: "scale(1.9)", transformOrigin: `${pos.x}% ${pos.y}%` } : undefined} />
    </button>
  );
}

export function MediaGallery({ media, title }) {
  const { lang } = useT();
  const [i, setI] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  useEffect(() => { if (!lightbox) return undefined; const h = (e) => e.key === "Escape" && setLightbox(false); window.addEventListener("keydown", h); return () => window.removeEventListener("keydown", h); }, [lightbox]);
  if (!media.length) return <div className="photo-frame aspect-[4/5]"><img src="/emblem-square.png" alt="" className="absolute inset-0 m-auto w-1/3 opacity-30" /></div>;
  const m = media[Math.min(i, media.length - 1)];
  const alt = m[`alt_${lang}`] || title;
  const go = (d) => setI((i + d + media.length) % media.length);
  return (
    <div data-testid="media-gallery">
      <div className="relative">
        {m.type === "video" ? <VideoPlayer key={m.id} m={m} alt={alt} /> : <Zoomable m={m} alt={alt} onOpen={() => setLightbox(true)} />}
        {media.length > 1 && <>
          <button onClick={() => go(-1)} className="absolute left-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-[#0C0B09]/60 text-[#E6D5AC]" aria-label="Anterior" data-testid="gallery-prev"><ChevronLeft className="w-5 h-5" /></button>
          <button onClick={() => go(1)} className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-full bg-[#0C0B09]/60 text-[#E6D5AC]" aria-label="Seguinte" data-testid="gallery-next"><ChevronRight className="w-5 h-5" /></button>
        </>}
      </div>
      {media.length > 1 && (
        <div className="mt-4 grid grid-cols-5 sm:grid-cols-6 gap-3">
          {media.map((x, n) => (
            <button key={x.id} onClick={() => setI(n)} className={`photo-frame aspect-square ring-1 transition-[box-shadow,opacity] duration-500 ${n === i ? "ring-[#C8AE6E]" : "ring-transparent opacity-60 hover:opacity-100"}`} data-testid={`gallery-thumb-${n}`}>
              {(x.sm || x.poster) && <img src={mediaUrl(x.sm || x.poster)} alt="" className="absolute inset-0 w-full h-full object-cover" />}
              {x.type === "video" && <Play className="absolute inset-0 m-auto w-5 h-5 text-[#E6D5AC]" />}
            </button>
          ))}
        </div>
      )}
      {lightbox && m.type === "photo" && (
        <div className="fixed inset-0 z-[60] bg-[#0C0B09]/95 flex items-center justify-center p-4" onClick={() => setLightbox(false)} data-testid="gallery-lightbox">
          <img src={mediaUrl(m.lg)} alt={alt} className="max-h-full max-w-full object-contain" />
          <button className="absolute top-5 right-5 text-[#E6D5AC]" aria-label="Fechar" data-testid="gallery-lightbox-close"><X className="w-6 h-6" /></button>
        </div>
      )}
    </div>
  );
}
